import { useRef } from 'react';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { Box, theme } from '@prose-ui';
import { useInView } from 'node_modules/react-intersection-observer/dist/index.mjs';

import BaseScene from 'Apps/Checkout/Scenes/BaseScene';

import { IngredientsSection } from 'Components/IngredientsSlider/IngredientsSection';
import { MetaData } from 'Components/MetaData';
import { StickyProductCTA } from 'Components/StickyProductCTA';

import { productsCategories, type productsSlugs } from 'constants/products';

import { Faq } from 'Blocks/Faq';
import { type ProductCardType, ProductsCarousel } from 'Blocks/ProductsCarousel';
import { Ticker } from 'Blocks/Ticker';

import ScrollTracker from 'Containers/ScrollTracker';

import { productPageContentBySlug } from 'assets/content/productPages';
import { productsMetadata } from 'assets/metadata/productsMetadata';
import productsStucturedData from 'assets/metadata/productsStructuredData';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent } from 'dux/tracking/actions';
import {
  getCategoryByProductSlug,
  getFilteredFetchedHaircareProducts,
  getFilteredFetchedSkincareProducts,
  getProductsContentWithGiftSetMergedWithFetchedProducts,
} from 'dux/products/selectors';
import { useFetchProductsList } from 'dux/products/hooks';

import { ProductHero } from './Blocks/ProductHero';
import { ProductReviews } from './Blocks/ProductReviews';
import { HowToUse } from './Components/HowToUse';

type ProductPageSlug = typeof productsSlugs.SCALP_SERUM;

type ProductPageProps = {
  productSlug: ProductPageSlug;
};

const usePageContent = (productSlug: ProductPageSlug) => {
  useFetchProductsList();
  const haircareProducts = useAppSelector(getFilteredFetchedHaircareProducts) as { type: string }[];
  const skincareProducts = useAppSelector(getFilteredFetchedSkincareProducts) as { type: string }[];
  const category = useAppSelector((state) => getCategoryByProductSlug(state, { productSlug })) as
    | typeof productsCategories.HAIRCARE
    | typeof productsCategories.SKINCARE;
  const productsCarouselContent = useAppSelector(
    getProductsContentWithGiftSetMergedWithFetchedProducts,
  ) as Record<string, ProductCardType>;

  const content = productPageContentBySlug[productSlug];
  const productsListByCategory = {
    [productsCategories.HAIRCARE]: haircareProducts,
    [productsCategories.SKINCARE]: skincareProducts,
  };
  const productCarouselCards = Object.keys(productsCarouselContent)
    .filter(
      (key) =>
        productsListByCategory[category]?.find((product) => product.type === key) &&
        key !== productSlug,
    )
    .map((key) => productsCarouselContent[key]) as ProductCardType[];

  return { content, productCarouselCards };
};

export const ProductPage = ({ productSlug }: ProductPageProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  const productCategory = useAppSelector((state) =>
    getCategoryByProductSlug(state, { productSlug }),
  );
  const parentRef = useRef<HTMLDivElement>(null);
  const [heroRef, heroInView] = useInView({ threshold: 0.7, initialInView: true });

  const dispatch = useAppDispatch();
  const {
    content: { ticker, howToUse, productCarousel, ingredientsSlider, faq, hero, productReviews },
    productCarouselCards,
  } = usePageContent(productSlug);

  const reviewRef = useRef<HTMLDivElement>(null);

  const scrollToReviews = () => {
    if (reviewRef.current) {
      reviewRef.current.scrollIntoView({
        behavior: 'smooth',
        block: isMobile ? 'center' : 'start',
      });
    }
    // Property 'scrollIntoView' does not exist on type 'never'.ts(2339)
  };

  return (
    <div ref={parentRef}>
      <MetaData
        description={productsMetadata[productSlug].description}
        structuredData={productsStucturedData[productSlug]}
        title={productsMetadata[productSlug].title}
      />
      <BaseScene footer justifyContent="normal">
        <ScrollTracker>
          <ProductHero
            accordion={hero.accordion}
            description={hero.description}
            heroRef={heroRef}
            onScrollToReviews={scrollToReviews}
            parentRef={parentRef}
            personalizationWorksBetter={hero.personalizationWorksBetter}
            productCategory={productCategory}
            productSlug={productSlug}
            slider={hero.slider}
            subtitle={hero.subtitle}
            title={hero.title}
            upsellModule={hero.upsellModule}
          />
          <Ticker
            backgroundColor={theme.colors.primary[400]}
            color={theme.colors.neutral[200]}
            content={ticker}
            showDash={false}
          />
          <Box
            backgroundColor="neutral.200"
            paddingX={{ mobile: 'none', tablet: 'none', desktop: '8x' }}
            paddingY={{ mobile: '15x', desktop: '30x' }}
            w="full"
          >
            <IngredientsSection
              content={ingredientsSlider}
              trackOnChange={() => dispatch(trackHeapEvent('Ingredients Module Carousel Scroll'))}
            />
          </Box>
          <HowToUse
            amount={howToUse.amount}
            description={howToUse.description}
            fitCover
            label={howToUse.label}
            posterImage={howToUse.posterImage}
            steps={howToUse.steps}
            title={howToUse.title}
            video={howToUse.video}
          />
          <ProductReviews ref={reviewRef} reviews={productReviews} />
          <Faq
            backgroundColor="white"
            dataTestId="skincare-pdp-faq"
            markupName="h3"
            questions={faq.questions}
            title={faq.title}
            variant="light"
          />
          <ProductsCarousel
            background={theme.colors.neutral[200]}
            content={{ headline: productCarousel.headline, cards: productCarouselCards }}
            margins="120px auto"
          />
          <StickyProductCTA
            dataFrom="product-module-sticky"
            productSlug={productSlug}
            shouldShow={!heroInView}
          />
        </ScrollTracker>
      </BaseScene>
    </div>
  );
};
